import type { OrderItem, Product, StockStatus } from "../types";

export type ImportRejection = {
  rowNumber: number;
  errors: string[];
  raw: Record<string, string>;
};

export type ImportRowMeta = {
  sourceRowNumber?: number;
};

export type ParsedImport<T> = {
  accepted: Array<T & ImportRowMeta>;
  headers: string[];
  rejected: ImportRejection[];
  rowCount: number;
};

function slug(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

function countDelimiter(line: string, delimiter: string) {
  let quoted = false;
  let count = 0;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const next = line[index + 1];

    if (char === '"' && quoted && next === '"') {
      index += 1;
      continue;
    }

    if (char === '"') {
      quoted = !quoted;
      continue;
    }

    if (!quoted && char === delimiter) {
      count += 1;
    }
  }

  return count;
}

function detectDelimiter(text: string) {
  const sample = text.split(/\r?\n/).find((line) => line.trim()) ?? text;
  const counts = [
    { delimiter: "\t", count: countDelimiter(sample, "\t") },
    { delimiter: ";", count: countDelimiter(sample, ";") },
    { delimiter: ",", count: countDelimiter(sample, ",") },
  ];

  return counts.sort((left, right) => right.count - left.count)[0]?.delimiter ?? ",";
}

function parseDelimited(text: string) {
  const delimiter = detectDelimiter(text);
  const rows: string[][] = [];
  let cell = "";
  let row: string[] = [];
  let quoted = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const next = text[index + 1];

    if (char === '"' && quoted && next === '"') {
      cell += '"';
      index += 1;
      continue;
    }

    if (char === '"') {
      quoted = !quoted;
      continue;
    }

    if (!quoted && char === delimiter) {
      row.push(cell.trim());
      cell = "";
      continue;
    }

    if (!quoted && (char === "\n" || char === "\r")) {
      if (char === "\r" && next === "\n") {
        index += 1;
      }
      row.push(cell.trim());
      if (row.some(Boolean)) {
        rows.push(row);
      }
      row = [];
      cell = "";
      continue;
    }

    cell += char;
  }

  row.push(cell.trim());
  if (row.some(Boolean)) {
    rows.push(row);
  }

  return rows;
}

function xmlDocumentFor(text: string) {
  if (typeof DOMParser === "undefined") {
    throw new Error("This environment cannot read XLSX files. Export the sheet as CSV or TSV and try again.");
  }

  const document = new DOMParser().parseFromString(text, "application/xml");

  if (document.getElementsByTagName("parsererror").length) {
    throw new Error("The XLSX file could not be read. Export the sheet again and retry.");
  }

  return document;
}

function xmlElements(root: Element | Document, localName: string) {
  return Array.from(root.getElementsByTagName("*")).filter(
    (element) => element.localName === localName,
  );
}

function xmlChildElements(root: Element, localName: string) {
  return Array.from(root.children).filter((element) => element.localName === localName);
}

function xlsxColumnIndex(reference: string, fallback: number) {
  const letters = reference.match(/^[A-Za-z]+/)?.[0];

  if (!letters) {
    return fallback;
  }

  return letters
    .toUpperCase()
    .split("")
    .reduce((total, letter) => total * 26 + letter.charCodeAt(0) - 64, 0) - 1;
}

function parseSharedStrings(xml: string) {
  const document = xmlDocumentFor(xml);

  return xmlElements(document, "si").map((item) =>
    xmlElements(item, "t")
      .map((text) => text.textContent ?? "")
      .join(""),
  );
}

function parseSheetRows(xml: string, sharedStrings: string[]) {
  const document = xmlDocumentFor(xml);

  return xmlElements(document, "row")
    .map((rowElement) => {
      const cells: string[] = [];

      xmlChildElements(rowElement, "c").forEach((cellElement, fallbackIndex) => {
        const type = cellElement.getAttribute("t") ?? "";
        const reference = cellElement.getAttribute("r") ?? "";
        const columnIndex = xlsxColumnIndex(reference, fallbackIndex);
        const rawValue = xmlChildElements(cellElement, "v")[0]?.textContent ?? "";

        if (type === "s") {
          cells[columnIndex] = sharedStrings[Number(rawValue)] ?? "";
          return;
        }

        if (type === "inlineStr") {
          cells[columnIndex] = xmlElements(cellElement, "t")
            .map((text) => text.textContent ?? "")
            .join("");
          return;
        }

        cells[columnIndex] = type === "b" ? (rawValue === "1" ? "TRUE" : "FALSE") : rawValue;
      });

      return cells.map((cell) => cell?.trim() ?? "");
    })
    .filter((row) => row.some(Boolean));
}

async function inflateZipEntry(bytes: Uint8Array) {
  if (typeof DecompressionStream === "undefined") {
    throw new Error("This browser cannot read compressed XLSX files. Export the sheet as CSV or TSV and try again.");
  }

  const input = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  const stream = new Blob([input]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function parseXlsxRows(file: File) {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const decoder = new TextDecoder();
  const eocdMinOffset = Math.max(0, bytes.length - 65557);
  let eocdOffset = -1;

  for (let offset = bytes.length - 22; offset >= eocdMinOffset; offset -= 1) {
    if (view.getUint32(offset, true) === 0x06054b50) {
      eocdOffset = offset;
      break;
    }
  }

  if (eocdOffset < 0) {
    throw new Error("The XLSX file is not a readable workbook.");
  }

  const entryCount = view.getUint16(eocdOffset + 10, true);
  const directoryOffset = view.getUint32(eocdOffset + 16, true);
  const entries = new Map<
    string,
    { compressedSize: number; localOffset: number; method: number; name: string }
  >();
  let cursor = directoryOffset;

  for (let index = 0; index < entryCount; index += 1) {
    if (view.getUint32(cursor, true) !== 0x02014b50) {
      throw new Error("The XLSX workbook index could not be read.");
    }

    const method = view.getUint16(cursor + 10, true);
    const compressedSize = view.getUint32(cursor + 20, true);
    const nameLength = view.getUint16(cursor + 28, true);
    const extraLength = view.getUint16(cursor + 30, true);
    const commentLength = view.getUint16(cursor + 32, true);
    const localOffset = view.getUint32(cursor + 42, true);
    const name = decoder.decode(bytes.slice(cursor + 46, cursor + 46 + nameLength));

    entries.set(name, { compressedSize, localOffset, method, name });
    cursor += 46 + nameLength + extraLength + commentLength;
  }

  async function readEntry(name: string) {
    const entry = entries.get(name);

    if (!entry) {
      return "";
    }

    if (view.getUint32(entry.localOffset, true) !== 0x04034b50) {
      throw new Error("The XLSX sheet data could not be opened.");
    }

    const nameLength = view.getUint16(entry.localOffset + 26, true);
    const extraLength = view.getUint16(entry.localOffset + 28, true);
    const dataStart = entry.localOffset + 30 + nameLength + extraLength;
    const compressed = bytes.slice(dataStart, dataStart + entry.compressedSize);
    const inflated =
      entry.method === 0
        ? compressed
        : entry.method === 8
          ? await inflateZipEntry(compressed)
          : undefined;

    if (!inflated) {
      throw new Error("The XLSX file uses a compression format this browser cannot read.");
    }

    return decoder.decode(inflated);
  }

  const sharedStringsXml = await readEntry("xl/sharedStrings.xml");
  const sharedStrings = sharedStringsXml ? parseSharedStrings(sharedStringsXml) : [];
  const sheetName =
    Array.from(entries.keys())
      .filter((name) => /^xl\/worksheets\/sheet\d+\.xml$/i.test(name))
      .sort((left, right) => left.localeCompare(right, undefined, { numeric: true }))[0] ??
    Array.from(entries.keys()).find((name) => /^xl\/worksheets\/.+\.xml$/i.test(name));

  if (!sheetName) {
    throw new Error("The XLSX workbook does not contain a readable worksheet.");
  }

  const rows = parseSheetRows(await readEntry(sheetName), sharedStrings);

  if (!rows.length) {
    throw new Error("The XLSX worksheet is empty.");
  }

  return rows;
}

function normalizeHeader(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function rowValue(row: Record<string, string>, aliases: string[]) {
  for (const alias of aliases) {
    const value = row[alias];
    if (value) {
      return value.trim();
    }
  }

  return "";
}

const productNameAliases = [
  "name",
  "product",
  "product_name",
  "item",
  "description",
  "naziv",
  "naziv_proizvoda",
  "proizvod",
  "artikl",
  "stavka",
  "opis",
];

const categoryAliases = [
  "category",
  "group",
  "department",
  "family",
  "kategorija",
  "grupa",
  "odjel",
  "odeljenje",
  "familija",
];

const unitAliases = [
  "unit",
  "uom",
  "measure",
  "unit_of_measure",
  "jedinica",
  "jm",
  "mjera",
  "mera",
  "jedinica_mjere",
  "jedinica_mere",
];

const priceAliases = [
  "price",
  "unit_price",
  "base_price",
  "cost",
  "net_price",
  "budget_price",
  "last_price",
  "cijena",
  "cena",
  "jedinicna_cijena",
  "jedinicna_cena",
  "nabavna_cijena",
  "nabavna_cena",
  "zadnja_cijena",
  "zadnja_cena",
  "budzet_cijena",
  "budzet_cena",
];

const supplierAliases = [
  "supplier",
  "supplier_name",
  "vendor",
  "company",
  "preferred_supplier",
  "dobavljac",
  "preferirani_dobavljac",
  "naziv_dobavljaca",
  "firma",
  "kompanija",
];

const noteAliases = [
  "note",
  "notes",
  "remark",
  "remarks",
  "napomena",
  "biljeska",
  "bilješka",
  "komentar",
];

const quantityAliases = [
  "quantity",
  "qty",
  "amount",
  "order_quantity",
  "stock",
  "stock_quantity",
  "last_ordered",
  "kolicina",
  "narucena_kolicina",
  "stanje",
  "zaliha",
  "na_stanju",
];

const stockStatusAliases = [
  "stock_status",
  "availability",
  "status",
  "dostupnost",
  "stanje_status",
];

function recognizedHeaderCount(headers: string[], aliases: string[]) {
  const recognized = new Set(aliases.map(normalizeHeader));
  return headers.filter((header) => recognized.has(header)).length;
}

function parseNumber(value: string) {
  if (!value) {
    return 0;
  }

  const numericText = value.replace(/[^\d,.-]/g, "").replace(/\s/g, "");
  const lastComma = numericText.lastIndexOf(",");
  const lastDot = numericText.lastIndexOf(".");
  const decimalMark = lastComma > lastDot ? "," : ".";
  const normalized = numericText
    .replace(new RegExp(`\\${decimalMark === "," ? "." : ","}`, "g"), "")
    .replace(decimalMark, ".");
  const match = normalized.match(/-?\d+(\.\d+)?/);

  return match ? Number(match[0]) : 0;
}

function normalizeStockStatus(value: string): StockStatus {
  const normalized = normalizeHeader(value);

  if (/unavailable|out|none|zero|disabled|nedostup|nema|rasprodato|nije_dostupno/.test(normalized)) {
    return "unavailable";
  }

  if (/low|limited|watch|backorder|malo|ogranic|nisko/.test(normalized)) {
    return "low";
  }

  return "available";
}

async function parseFile(
  file: File,
  options: {
    fallbackHeaders: string[];
    recognizedHeaders: string[];
  },
) {
  const lowerName = file.name.toLowerCase();
  const isXlsx =
    lowerName.endsWith(".xlsx") ||
    file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  const isLegacyExcel = lowerName.endsWith(".xls");

  if (isLegacyExcel) {
    throw new Error("Legacy .xls files are not supported. Save the workbook as .xlsx, CSV, or TSV and try again.");
  }

  const rows = isXlsx ? await parseXlsxRows(file) : parseDelimited(await file.text());
  const firstRowHeaders = (rows[0] ?? []).map(normalizeHeader);
  const hasRecognizedHeader = recognizedHeaderCount(firstRowHeaders, options.recognizedHeaders) >= 2;
  const headers = hasRecognizedHeader
    ? firstRowHeaders
    : options.fallbackHeaders.map(normalizeHeader);
  const dataRows = hasRecognizedHeader ? rows.slice(1) : rows;
  const firstDataRowNumber = hasRecognizedHeader ? 2 : 1;

  return {
    dataRows,
    firstDataRowNumber,
    headers,
    records: dataRows.map((cells) =>
      Object.fromEntries(headers.map((header, index) => [header, cells[index]?.trim() ?? ""])),
    ),
  };
}

export async function parseSupplierCatalogFile(
  file: File,
  fallbackSupplierName: string,
): Promise<ParsedImport<Product>> {
  const { firstDataRowNumber, headers, records } = await parseFile(file, {
    fallbackHeaders: ["product_name", "category", "unit", "price", "supplier", "stock_quantity", "stock_status"],
    recognizedHeaders: [
      ...productNameAliases,
      ...categoryAliases,
      ...unitAliases,
      ...priceAliases,
      ...supplierAliases,
      ...quantityAliases,
      ...stockStatusAliases,
    ],
  });
  const accepted: Array<Product & ImportRowMeta> = [];
  const rejected: ImportRejection[] = [];

  records.forEach((row, index) => {
    const rowNumber = firstDataRowNumber + index;
    const name = rowValue(row, productNameAliases);
    const category = rowValue(row, categoryAliases);
    const unit = rowValue(row, unitAliases);
    const price = parseNumber(
      rowValue(row, priceAliases),
    );
    const supplier =
      rowValue(row, supplierAliases) || fallbackSupplierName;
    const lastOrderedKg = parseNumber(
      rowValue(row, quantityAliases),
    );
    const stockStatus = normalizeStockStatus(
      rowValue(row, stockStatusAliases),
    );
    const errors = [
      !name ? "Missing product name" : "",
      !category ? "Missing category" : "",
      !unit ? "Missing unit" : "",
      price <= 0 ? "Missing positive unit price" : "",
      !supplier ? "Missing supplier name" : "",
    ].filter(Boolean);

    if (errors.length) {
      rejected.push({ rowNumber, errors, raw: row });
      return;
    }

    accepted.push({
      id: `import-${slug(supplier)}-${slug(name)}-${rowNumber}`,
      name,
      supplier,
      category,
      unit,
      price,
      currency: "BAM",
      stockStatus,
      lastOrderedKg,
      sourceRowNumber: rowNumber,
    });
  });

  return { accepted, headers, rejected, rowCount: records.length };
}

export async function parseRestaurantOrderGuideFile(file: File): Promise<ParsedImport<OrderItem>> {
  const { firstDataRowNumber, headers, records } = await parseFile(file, {
    fallbackHeaders: ["product_name", "quantity", "unit", "price", "supplier", "category", "note"],
    recognizedHeaders: [
      ...productNameAliases,
      ...quantityAliases,
      ...unitAliases,
      ...priceAliases,
      ...supplierAliases,
      ...categoryAliases,
      ...noteAliases,
    ],
  });
  const accepted: Array<OrderItem & ImportRowMeta> = [];
  const rejected: ImportRejection[] = [];

  records.forEach((row, index) => {
    const rowNumber = firstDataRowNumber + index;
    const name = rowValue(row, productNameAliases);
    const quantity = parseNumber(
      rowValue(row, quantityAliases),
    );
    const unit = rowValue(row, unitAliases);
    const unitPrice = parseNumber(
      rowValue(row, priceAliases),
    );
    const supplier = rowValue(row, supplierAliases);
    const category = rowValue(row, categoryAliases);
    const note = rowValue(row, noteAliases);
    const errors = [
      !name ? "Missing product name" : "",
      quantity <= 0 ? "Missing positive quantity" : "",
      !unit ? "Missing unit" : "",
    ].filter(Boolean);

    if (errors.length) {
      rejected.push({ rowNumber, errors, raw: row });
      return;
    }

    accepted.push({
      productId: `import-guide-${slug(supplier || "quote")}-${slug(name)}-${rowNumber}`,
      name,
      quantity,
      unit,
      unitPrice,
      supplier: supplier || undefined,
      category: category || undefined,
      note: note || undefined,
      source: supplier ? "reorder" : "quote",
      sourceRowNumber: rowNumber,
    });
  });

  return { accepted, headers, rejected, rowCount: records.length };
}
